import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useSupplierService } from 'services/suppliers';
import { Supplier } from 'types/supplier';
import Button from 'components/Button';
import styles from './Suppliers.module.scss';

interface EditSupplierModalProps {
  show: boolean;
  onHide: () => void;
  supplier: Supplier | null;
  onSuccess: (supplier: Supplier) => void;
  onError: (message: string) => void;
}

function EditSupplierModal({
  show,
  onHide,
  supplier,
  onSuccess,
  onError,
}: EditSupplierModalProps) {
  const supplierService = useSupplierService();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [tinNumber, setTinNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (supplier) {
      setName(supplier.name || '');
      setEmail(supplier.email || '');
      setPhone(supplier.phone || '');
      setAddress(supplier.address || '');
      setTinNumber(supplier.tinNumber || '');
      setNotes(supplier.notes || '');
    }
  }, [supplier, show]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!supplier) return;

    if (!name.trim()) {
      onError('Supplier name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedSupplier = await supplierService.updateSupplier(
        supplier.id,
        {
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          tinNumber: tinNumber.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      );
      onSuccess(updatedSupplier);
      onHide();
    } catch (error) {
      onError(
        error instanceof Error ? error.message : 'Failed to update supplier',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!supplier) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
      backdrop="static"
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Edit Supplier</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(e);
          }}
        >
          <Form.Group controlId="supplierName" className={styles.formGroup}>
            <Form.Label className={styles.label}>Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Supplier name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="supplierEmail" className={styles.formGroup}>
            <Form.Label className={styles.label}>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="supplier@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="supplierPhone" className={styles.formGroup}>
            <Form.Label className={styles.label}>Phone</Form.Label>
            <Form.Control
              type="text"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="supplierAddress" className={styles.formGroup}>
            <Form.Label className={styles.label}>Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="123 Main St, City, State 12345"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group
            controlId="supplierTinNumber"
            className={styles.formGroup}
          >
            <Form.Label className={styles.label}>TIN Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="123-456-789"
              value={tinNumber}
              onChange={(e) => setTinNumber(e.target.value)}
              className={styles.input}
            />
          </Form.Group>

          <Form.Group controlId="supplierNotes" className={styles.formGroup}>
            <Form.Label className={styles.label}>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Additional notes about the supplier..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={styles.textarea}
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={onHide}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Supplier'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditSupplierModal;
